import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Dna, ImagePlus, Loader2, Star, Trash2, X } from "lucide-react";
import {
  createAdminAnimal,
  createAdminEvent,
  deleteAdminAnimal,
  deleteAdminEvent,
  deleteAdminReview,
  getAdminAnalytics,
  getAdminReviews,
  getEvents,
  getLineage,
  getSettings,
  updateAdminAnimal,
  updateAdminEvent,
  updateAdminReview,
  updateAdminSettings,
  uploadAdminImage,
} from "@/lib/site";
import { useEffect } from "react";
import { Stars } from "@/components/Reviews";

export function TrafficAdmin() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getAdminAnalytics(localStorage.getItem("gf_admin") || "")
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <p className="text-sm text-sage">Kunne ikke hente trafikdata.</p>;
  if (!data)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-forest" />
      </div>
    );

  const maxViews = Math.max(1, ...data.daily.map((d) => d.views));
  const stats = [
    ["Sidevisninger i dag", data.today],
    ["Sidste 7 dage", data.week],
    ["Unikke besøgende (14 dg.)", data.unique_14d],
    ["Sidevisninger i alt", data.total],
  ];

  return (
    <div data-testid="traffic-admin" className="mt-8 space-y-10">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map(([lbl, val], i) => (
          <div key={lbl} data-testid={`traffic-stat-${i}`} className="border border-white/10 bg-moss p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-sage">{lbl}</p>
            <p className="mt-2 font-serif text-3xl text-cream">{Number(val).toLocaleString("da-DK")}</p>
          </div>
        ))}
      </div>

      <div>
        <p className={label}>Sidevisninger — sidste 14 dage</p>
        <div data-testid="traffic-chart" className="mt-4 flex h-40 items-end gap-1.5 border border-white/10 bg-moss p-4">
          {data.daily.map((d) => {
            const [y, m, day] = d.date.split("-");
            return (
              <div key={d.date} className="group relative flex flex-1 flex-col items-center justify-end self-stretch">
                <span className="pointer-events-none absolute -top-1 -translate-y-full whitespace-nowrap border border-white/10 bg-ink px-2 py-1 font-mono text-[10px] text-cream opacity-0 transition-opacity group-hover:opacity-100">
                  {day}/{m}: {d.views} visninger · {d.uniques} unikke
                </span>
                <div
                  data-testid={`traffic-bar-${d.date}`}
                  className="w-full bg-forest transition-colors duration-200 group-hover:bg-pale"
                  style={{ height: `${Math.max(3, Math.round((d.views / maxViews) * 100))}%` }}
                />
                <span className="mt-2 font-mono text-[9px] text-sage/70">{day}/{m}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <p className={label}>Mest besøgte sider (14 dage)</p>
          <div data-testid="traffic-top-pages" className="mt-4 divide-y divide-white/10 border border-white/10">
            {data.top_pages.length === 0 && (
              <p className="p-4 text-sm text-sage">Ingen besøg endnu — de dukker op her.</p>
            )}
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between px-4 py-3">
                <span className="truncate font-mono text-xs text-cream">{p.path}</span>
                <span className="ml-4 shrink-0 font-mono text-xs text-sage">
                  {p.views} visninger · {p.uniques} unikke
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className={label}>Her kommer besøgende fra</p>
          <div data-testid="traffic-top-refs" className="mt-4 divide-y divide-white/10 border border-white/10">
            {data.top_refs.length === 0 && (
              <p className="p-4 text-sm text-sage">Ingen henvisninger endnu.</p>
            )}
            {data.top_refs.map((r) => (
              <div key={r.ref} className="flex items-center justify-between px-4 py-3">
                <span className="truncate font-mono text-xs text-cream">{r.ref}</span>
                <span className="ml-4 shrink-0 font-mono text-xs text-sage">{r.views} besøg</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-sage/70">
            Målingen er anonym og uden cookies: besøgende tælles via et anonymt fingeraftryk, og
            data slettes automatisk efter 90 dage. Direkte besøg og besøg uden henvisning tælles
            ikke under henvisninger.
          </p>
        </div>
      </div>
    </div>
  );
}

const field =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";
const label = "font-mono text-[10px] uppercase tracking-[0.15em] text-sage";

export function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ date: "", name: "", location: "", entrance: "", attending: false });
  const [saving, setSaving] = useState(false);

  const load = () => getEvents().then(setEvents).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const toggleAttending = async (e) => {
    try {
      await updateAdminEvent(e.id, { ...e, attending: !e.attending });
      toast.success(e.attending ? `${e.name}: ikke længere markeret` : `${e.name}: "Mød os her" slået til`);
      load();
    } catch {
      toast.error("Kunne ikke opdatere messen");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Slet "${e.name} — ${e.location}"?`)) return;
    try {
      await deleteAdminEvent(e.id);
      toast.success("Messe slettet");
      load();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const add = async (ev) => {
    ev.preventDefault();
    if (!form.date || !form.name.trim() || !form.location.trim())
      return toast.error("Dato, navn og sted skal udfyldes");
    setSaving(true);
    try {
      await createAdminEvent(form);
      toast.success("Messe tilføjet");
      setForm({ date: "", name: "", location: "", entrance: "", attending: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke tilføje");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-events" className="mt-8">
      <p className="max-w-xl text-sm leading-relaxed text-sage">
        Slå <span className="text-pale">Mød os her</span> til på de messer, hvor I har stand.
        I ugen op til en markeret messe kan kunder vælge gratis afhentning på messen i kassen.
      </p>

      <div className="mt-6">
        {events.map((e) => (
          <div
            key={e.id}
            data-testid={`admin-event-${e.id}`}
            className="flex flex-wrap items-center gap-4 border-b border-white/10 py-4"
          >
            <span className="font-mono text-xs text-sage">{e.date}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-cream">
              {e.name} <span className="text-sage">· {e.location}</span>
            </span>
            <button
              data-testid={`admin-event-attending-${e.id}`}
              onClick={() => toggleAttending(e)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                e.attending
                  ? "border-forest bg-forest text-cream"
                  : "border-white/15 text-sage hover:border-white/40 hover:text-cream"
              }`}
            >
              <Star className={`h-3 w-3 ${e.attending ? "fill-cream" : ""}`} /> Mød os her
            </button>
            <button
              data-testid={`admin-event-delete-${e.id}`}
              onClick={() => remove(e)}
              className="flex items-center gap-1.5 rounded-full border border-red-900/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300"
            >
              <Trash2 className="h-3 w-3" /> Slet
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={add} data-testid="admin-event-form" className="mt-8 border border-white/10 bg-ink/40 p-6">
        <p className={label}>Tilføj messe</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            data-testid="admin-event-date"
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className={`${field} [color-scheme:dark]`}
          />
          <input
            data-testid="admin-event-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Navn, fx Reptil & Terrarie Messe"
            className={field}
          />
          <input
            data-testid="admin-event-location"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="Sted, fx Hedensted Centret, Hedensted"
            className={field}
          />
          <input
            data-testid="admin-event-entrance"
            value={form.entrance}
            onChange={(e) => setForm((f) => ({ ...f, entrance: e.target.value }))}
            placeholder="Entré (valgfri), fx Entré 85 kr"
            className={field}
          />
        </div>
        <label className="mt-4 flex items-center gap-3 text-sm text-sage">
          <input
            type="checkbox"
            data-testid="admin-event-attending-new"
            checked={form.attending}
            onChange={(e) => setForm((f) => ({ ...f, attending: e.target.checked }))}
            className="h-4 w-4 accent-forest"
          />
          Vi har stand her (vis "Mød os her")
        </label>
        <button
          type="submit"
          data-testid="admin-event-add"
          disabled={saving}
          className="mt-5 flex items-center gap-3 rounded-full bg-forest px-7 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tilføj messe"}
        </button>
      </form>
    </div>
  );
}

function HeroImageSlot({ title, hint, value, onUploaded, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAdminImage(file);
      onUploaded(res.url);
      toast.success("Billede uploadet — husk at gemme");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className={label}>{title}</p>
      <p className="mt-1 text-xs text-sage/70">{hint}</p>
      <div className="mt-3 flex items-center gap-4">
        {value && (
          <img src={value} alt="" className="h-24 w-24 border border-white/10 object-cover" />
        )}
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-24 w-24 items-center justify-center border border-dashed border-white/25 text-sage transition-colors hover:border-forest hover:text-cream disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={pick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

export function ForsideAdmin() {
  const [settings, setSettings] = useState({ hero_portrait: "", hero_bg: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateAdminSettings(settings);
      toast.success("Forsiden er opdateret");
    } catch {
      toast.error("Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-forside" className="mt-8">
      <p className="max-w-xl text-sm leading-relaxed text-sage">
        Skift billederne på forsiden — fx et frisk foto af jeres nyeste opdræt. Tomme felter
        bruger standardbillederne.
      </p>
      <div className="mt-8 grid grid-cols-1 gap-10 sm:grid-cols-2">
        <HeroImageSlot
          title="Gekko-portræt"
          hint="Vises i højre side af hero-sektionen"
          value={settings.hero_portrait}
          onUploaded={(url) => setSettings((s) => ({ ...s, hero_portrait: url }))}
          testid="admin-hero-portrait"
        />
        <HeroImageSlot
          title="Baggrund"
          hint="Stort, mørkt baggrundsbillede bag teksten"
          value={settings.hero_bg}
          onUploaded={(url) => setSettings((s) => ({ ...s, hero_bg: url }))}
          testid="admin-hero-bg"
        />
      </div>
      <button
        data-testid="admin-forside-save"
        onClick={save}
        disabled={saving}
        className="mt-8 flex items-center gap-3 rounded-full bg-forest px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gem forside"}
      </button>
    </div>
  );
}

export function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);

  const load = () => getAdminReviews().then(setReviews).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const approve = async (r) => {
    try {
      await updateAdminReview(r.id, !r.approved);
      toast.success(r.approved ? "Anmeldelse skjult" : "Anmeldelse godkendt");
      load();
    } catch {
      toast.error("Kunne ikke opdatere");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Slet anmeldelsen fra ${r.name}?`)) return;
    try {
      await deleteAdminReview(r.id);
      toast.success("Anmeldelse slettet");
      load();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  return (
    <div data-testid="admin-reviews" className="mt-8">
      <p className="max-w-xl text-sm leading-relaxed text-sage">
        Nye anmeldelser er skjulte, indtil du godkender dem — så vises de på varesiden.
      </p>
      {reviews.length === 0 && <p className="mt-6 text-sm text-sage">Ingen anmeldelser endnu.</p>}
      {reviews.map((r) => (
        <div
          key={r.id}
          data-testid={`admin-review-${r.id}`}
          className="mt-4 flex flex-wrap items-center gap-4 border-b border-white/10 pb-4"
        >
          <Stars value={r.rating} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-cream">
              {r.name} <span className="text-sage">· {r.slug}</span>
            </p>
            <p className="truncate text-xs text-sage">{r.text}</p>
          </div>
          <span
            className={`font-mono text-[10px] uppercase tracking-[0.15em] ${
              r.approved ? "text-pale" : "text-bark"
            }`}
          >
            {r.approved ? "Godkendt" : "Afventer"}
          </span>
          <button
            data-testid={`admin-review-approve-${r.id}`}
            onClick={() => approve(r)}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
              r.approved
                ? "border-white/15 text-sage hover:border-white/40 hover:text-cream"
                : "border-forest bg-forest text-cream hover:bg-pale hover:text-ink"
            }`}
          >
            <Check className="h-3 w-3" /> {r.approved ? "Skjul" : "Godkend"}
          </button>
          <button
            data-testid={`admin-review-delete-${r.id}`}
            onClick={() => remove(r)}
            className="flex items-center gap-1.5 rounded-full border border-red-900/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300"
          >
            <Trash2 className="h-3 w-3" /> Slet
          </button>
        </div>
      ))}
    </div>
  );
}

const LINEAGE_LINES = [
  ["cappuccino", "Cappuccino"],
  ["axanthic", "Axanthic"],
  ["tri-color", "Tri-color"],
  ["confetti", "Confetti Dalmatian"],
];
const GENERATIONS = ["P1", "F1", "F2", "F3"];

function AnimalEditor({ animal, animals, onClose, onSaved }) {
  const isEdit = Boolean(animal);
  const [form, setForm] = useState({
    name: animal?.name || "",
    line: animal?.line || "cappuccino",
    generation: animal?.generation || "P1",
    morph: animal?.morph || "",
    parents: animal?.parents || [],
    image: animal?.image || "",
  });
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const tid = isEdit ? `animal-edit-${animal.id}` : "animal-create";

  const possibleParents = animals.filter(
    (a) => a.line === form.line && a.id !== animal?.id
  );

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAdminImage(file);
      setForm((f) => ({ ...f, image: res.url }));
      toast.success("Billede uploadet");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
    }
  };

  const toggleParent = (id) =>
    setForm((f) => ({
      ...f,
      parents: f.parents.includes(id)
        ? f.parents.filter((p) => p !== id)
        : f.parents.length >= 2
          ? f.parents
          : [...f.parents, id],
    }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Navn mangler");
    setSaving(true);
    const payload = { ...form, name: form.name.trim() };
    try {
      if (isEdit) {
        await updateAdminAnimal(animal.id, payload);
        toast.success(`${form.name} opdateret`);
      } else {
        await createAdminAnimal(payload);
        toast.success(`${form.name} tilføjet stamtræet`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/80 backdrop-blur-sm" data-testid={`${tid}-modal`}>
      <div className="mx-auto my-16 w-full max-w-xl border border-white/10 bg-moss p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl tracking-tight text-cream">
            {isEdit ? `Rediger: ${animal.name}` : "Nyt dyr i stamtræet"}
          </h2>
          <button type="button" data-testid={`${tid}-close`} onClick={onClose} className="text-sage transition-colors hover:text-cream">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={save} className="mt-8 space-y-5" data-testid={`${tid}-form`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={label}>Navn</p>
              <input
                data-testid={`${tid}-name`}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={`${field} mt-2`}
                placeholder="Fx Kaffe"
              />
            </div>
            <div>
              <p className={label}>Linje</p>
              <select
                data-testid={`${tid}-line`}
                value={form.line}
                onChange={(e) => setForm((f) => ({ ...f, line: e.target.value, parents: [] }))}
                className={`${field} mt-2`}
              >
                {LINEAGE_LINES.map(([v, l]) => (
                  <option key={v} value={v} className="bg-ink">{l}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={label}>Generation</p>
              <select
                data-testid={`${tid}-generation`}
                value={form.generation}
                onChange={(e) => setForm((f) => ({ ...f, generation: e.target.value }))}
                className={`${field} mt-2`}
              >
                {GENERATIONS.map((g) => (
                  <option key={g} value={g} className="bg-ink">
                    {g}{g === "P1" ? " — avlsdyr" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className={label}>Morf (fx Cappuccino Lillywhite)</p>
              <input
                data-testid={`${tid}-morph`}
                value={form.morph}
                onChange={(e) => setForm((f) => ({ ...f, morph: e.target.value }))}
                className={`${field} mt-2`}
              />
            </div>
          </div>

          <div>
            <p className={label}>Billede</p>
            <div className="mt-2 flex items-center gap-4">
              {form.image && (
                <img src={form.image} alt="" className="h-20 w-20 border border-white/10 object-cover" />
              )}
              <button
                type="button"
                data-testid={`${tid}-image-upload`}
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="flex h-20 w-20 items-center justify-center border border-dashed border-white/25 text-sage transition-colors hover:border-forest hover:text-cream disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              </button>
            </div>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" data-testid={`${tid}-image-input`} />
          </div>

          {form.generation !== "P1" && possibleParents.length > 0 && (
            <div>
              <p className={label}>Forældre (vælg op til 2)</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {possibleParents.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`${tid}-parent-${p.id}`}
                    onClick={() => toggleParent(p.id)}
                    className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                      form.parents.includes(p.id)
                        ? "border-forest bg-forest text-cream"
                        : "border-white/15 text-sage hover:border-white/40"
                    }`}
                  >
                    {p.name} ({p.generation})
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              data-testid={`${tid}-save`}
              disabled={saving}
              className="flex items-center gap-3 rounded-full bg-forest px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Gem ændringer" : "Tilføj dyr"}
            </button>
            <button
              type="button"
              data-testid={`${tid}-cancel`}
              onClick={onClose}
              className="rounded-full border border-white/15 px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:border-white/40 hover:text-cream"
            >
              Annuller
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function LineageAdmin() {
  const [animals, setAnimals] = useState([]);
  const [lineFilter, setLineFilter] = useState("cappuccino");
  const [editing, setEditing] = useState(null); // null | { animal | undefined }

  const load = () => getLineage().then(setAnimals).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const remove = async (a) => {
    if (!window.confirm(`Fjern "${a.name}" fra stamtræet?`)) return;
    try {
      await deleteAdminAnimal(a.id);
      toast.success(`${a.name} fjernet`);
      load();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const visible = animals.filter((a) => a.line === lineFilter);

  return (
    <div data-testid="admin-lineage" className="mt-8">
      <p className="max-w-xl text-sm leading-relaxed text-sage">
        Byg stamtræet for hver linje: upload et billede, skriv et navn, vælg generation —
        og kobl afkom på deres forældre. Så enkelt er det.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {LINEAGE_LINES.map(([v, l]) => (
          <button
            key={v}
            data-testid={`admin-lineage-line-${v}`}
            onClick={() => setLineFilter(v)}
            className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
              lineFilter === v
                ? "border-forest bg-forest text-cream"
                : "border-white/15 text-sage hover:border-white/40"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <button
        data-testid="admin-lineage-add"
        onClick={() => setEditing({ animal: null })}
        className="mt-6 flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink"
      >
        <Dna className="h-4 w-4" /> Tilføj dyr
      </button>

      <div className="mt-6">
        {visible.length === 0 && (
          <p className="text-sm text-sage">Ingen dyr i denne linje endnu — tilføj det første.</p>
        )}
        {visible.map((a) => (
          <div
            key={a.id}
            data-testid={`admin-animal-${a.id}`}
            className="flex flex-wrap items-center gap-4 border-b border-white/10 py-3"
          >
            {a.image ? (
              <img src={a.image} alt="" className="h-12 w-12 border border-white/10 object-cover" />
            ) : (
              <span className="flex h-12 w-12 items-center justify-center border border-white/10 text-sage/40">
                <Dna className="h-5 w-5" />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-sm text-cream">
              {a.name} <span className="text-sage">· {a.morph || "—"}</span>
            </span>
            <span className="rounded-full border border-forest/50 px-3 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-pale">
              {a.generation}
            </span>
            <span className="font-mono text-[10px] text-sage/60">
              {(a.parents || []).length} forældre
            </span>
            <button
              data-testid={`admin-animal-edit-${a.id}`}
              onClick={() => setEditing({ animal: a })}
              className="rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sage transition-colors hover:border-white/40 hover:text-cream"
            >
              Rediger
            </button>
            <button
              data-testid={`admin-animal-delete-${a.id}`}
              onClick={() => remove(a)}
              className="flex items-center gap-1.5 rounded-full border border-red-900/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300"
            >
              <Trash2 className="h-3 w-3" /> Slet
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <AnimalEditor
          animal={editing.animal}
          animals={animals}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
